import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { Award, GraduationCap, Heart } from 'lucide-react';

const A_COPY = {
  es: { role: 'Profesora de Español · Examinadora DELE', world: 'Enseñando español a estudiantes de todo el mundo',
        years: '10+ Años de Experiencia', studies: 'Licenciada en Geografía con Máster en Educación' },
  de: { role: 'Spanischlehrerin · DELE-Prüferin', world: 'Spanischunterricht für Lernende aus aller Welt',
        years: 'Über 10 Jahre Erfahrung', studies: 'Studium der Geografie + Master in Pädagogik' },
  en: { role: 'Spanish Teacher · DELE Examiner', world: 'Teaching Spanish to students all over the world',
        years: '10+ Years of Experience', studies: 'Degree in Geography + Master’s in Education' },
};

export const About = () => {
  const { t, language } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23A8D8EA' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{t.about.subtitle}</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6">
              {t.about.title}
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image Column */}
            <motion.div variants={itemVariants} className="relative">
              <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/cecilia.jpg"
                  alt="Cecilia Mateo García — profesora de español online"
                  data-testid="about-cecilia-photo"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Name overlay */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6">
                  <p className="text-white text-2xl font-bold drop-shadow-md">Cecilia Mateo García</p>
                  <p className="text-white/90 text-sm mt-1">{A_COPY[language]?.role || A_COPY.es.role}</p>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <motion.div
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: 'linear'
                }}
                className="absolute -top-8 -right-8 w-24 h-24 bg-[#A8D8EA] rounded-full opacity-50 blur-xl"
              />
              <motion.div
                animate={{
                  rotate: [360, 0],
                }}
                transition={{
                  duration: 15,
                  repeat: Infinity,
                  ease: 'linear'
                }}
                className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#F5A6A4] rounded-full opacity-50 blur-xl"
              />
            </motion.div>

            {/* Content Column */}
            <motion.div variants={itemVariants} className="space-y-6">
              {String(t.about.description).split('\n\n').map((p, i) => (
                <p key={i} className="text-lg text-gray-700 leading-relaxed">
                  {p}
                </p>
              ))}

              {/* Credentials Cards */}
              <div className="grid grid-cols-1 gap-4 mt-8">
                <motion.div
                  whileHover={{ scale: 1.02, x: 10 }}
                  className="flex items-start space-x-4 p-6 bg-gradient-to-r from-[#C8E7F2] to-[#E8F5FA] rounded-2xl border border-[#A8D8EA]/50 shadow-md"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-[#7DBDD9] rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#4A4A4A] mb-1">{A_COPY[language]?.years || A_COPY.es.years}</h4>
                    <p className="text-gray-600 text-sm">{A_COPY[language]?.world || A_COPY.es.world}</p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02, x: 10 }}
                  className="flex items-start space-x-4 p-6 bg-gradient-to-r from-[#FFE5E4] to-[#FFF0EF] rounded-2xl border border-[#F5A6A4]/50 shadow-md"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-[#E38987] rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#4A4A4A] mb-1">{A_COPY[language]?.studies || A_COPY.es.studies}</h4>
                    <p className="text-gray-600 text-sm">{t.about.credentials}</p>
                  </div>
                </motion.div>
              </div>

              {/* Quote */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative mt-8 p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl border-l-4 border-[#F5A6A4] shadow-lg"
              >
                <Heart className="absolute top-4 right-4 w-6 h-6 text-[#F5A6A4]/40" />
                <p className="text-xl italic text-gray-700 font-medium leading-relaxed">
                  "{t.about.quote}"
                </p>
                <p className="text-right text-sm text-gray-500 mt-4 font-semibold">- Cecilia Mateo García</p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
